import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { ActionItemStatus, markInTransitToBuyerFormSchema } from '@ekhodealer/ekho-common';
import {
	Button,
	DateInput,
	SkeletonRect,
	Stack,
	Text,
	TextInput,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { dateValueToTimestamp,timestampToDateValue } from '@common/lib/time';

import { useGetMarkInTransitToBuyer } from '../../../api/progress/useGetMarkInTransitToBuyer';
import { useMarkInTransitToBuyer } from '../../../api/progress/useMarkInTransitToBuyer';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkInTransitToBuyerDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	estimatedDeliveryDate?: number;
	fulfillmentDetails?: {
		vehicleTrackingReference?: string;
		contactNumber?: string;
		companyName?: string;
	};
}

/**
 * MarkInTransitToBuyerDetail form component for marking a vehicle as in transit to buyer.
 * Displays a form with estimated delivery date (optional), tracking reference, contact number, and company name (optional).
 */
export const MarkInTransitToBuyerDetail = ({ actionItem }: MarkInTransitToBuyerDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markInTransitMutation = useMarkInTransitToBuyer(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetMarkInTransitToBuyer(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markInTransitToBuyerFormSchema),
		defaultValues: {
			estimatedDeliveryDate: undefined as unknown as number,
			fulfillmentDetails: {
				vehicleTrackingReference: '',
				contactNumber: '',
				companyName: '',
			},
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData) {
			reset({
				estimatedDeliveryDate: existingData.estimatedDeliveryDate,
				fulfillmentDetails: {
					vehicleTrackingReference:
						existingData.fulfillmentDetails?.vehicleTrackingReference || '',
					contactNumber: existingData.fulfillmentDetails?.contactNumber || '',
					companyName: existingData.fulfillmentDetails?.companyName || '',
				},
			});
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await markInTransitMutation.mutateAsync({
				estimatedDeliveryDate: data.estimatedDeliveryDate,
				fulfillmentDetails: {
					vehicleTrackingReference:
						data.fulfillmentDetails?.vehicleTrackingReference?.trim() || undefined,
					contactNumber: data.fulfillmentDetails?.contactNumber?.trim() || undefined,
					companyName: data.fulfillmentDetails?.companyName?.trim() || undefined,
				},
			});
			toast({
				message: 'Vehicle marked as in transit to buyer successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: 'Failed to mark vehicle as in transit to buyer';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Enter the estimated delivery date and optional transit details. Note: an email will be sent to the buyer communicating the updated status.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{isLoadingData ? (
					<SkeletonRect width='100%' height={200} />
				) : (
					<>
					{/* ESTIMATED DELIVERY DATE */}
					<Controller
						name='estimatedDeliveryDate'
						control={control}
						render={({ field: { onChange, value } }) => (
							<DateInput
								label='Estimated delivery date'
								isReadOnly={
									isSubmitting ||
									markInTransitMutation.isPending ||
									actionItem.status === ActionItemStatus.COMPLETE
								}
								variant='outline'
								size='large'
								value={timestampToDateValue(value)}
								onChange={(val) => {
									const timestamp = dateValueToTimestamp(val);
									onChange(timestamp);
								}}
								hasError={errors.estimatedDeliveryDate?.message}
							/>
						)}
					/>

						{/* TRACKING REFERENCE */}
						<Controller
							name='fulfillmentDetails.vehicleTrackingReference'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Tracking reference (optional)</Text>
									<TextInput
										placeholder='Enter tracking reference'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										isDisabled={
											isSubmitting ||
											markInTransitMutation.isPending ||
											actionItem.status === ActionItemStatus.COMPLETE
										}
									/>
								</Stack>
							)}
						/>

						{/* CONTACT NUMBER */}
						<Controller
							name='fulfillmentDetails.contactNumber'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Contact number (optional)</Text>
									<TextInput
										placeholder='Enter contact number'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										isDisabled={
											isSubmitting ||
											markInTransitMutation.isPending ||
											actionItem.status === ActionItemStatus.COMPLETE
										}
									/>
								</Stack>
							)}
						/>

						{/* COMPANY NAME */}
						<Controller
							name='fulfillmentDetails.companyName'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Company name (optional)</Text>
									<TextInput
										placeholder='Enter company name'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										isDisabled={
											isSubmitting ||
											markInTransitMutation.isPending ||
											actionItem.status === ActionItemStatus.COMPLETE
										}
									/>
								</Stack>
							)}
						/>
					</>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || markInTransitMutation.isPending}
						isDisabled={isLoadingData}>
						{actionItem.config.buttonTitle || 'Mark in transit to buyer'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
